import {
  Box,
  FormControl,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { SettingsDangerZoneRow } from "~/components/settings/DangerZoneWrapper";
import { OverviewFieldRow } from "~/features/settings/components/OverviewFieldRow";
import {
  settingsSelectSx,
  settingsSiteSelectMaxWidthPx
} from "~/features/settings/settings.styles";
import {
  selectDropdownWorkstationId,
  setDropdownWorkstationId
} from "~/features/workstationConfig/workstationConfig.slice";
import { useSetThisWorkstation } from "~/hooks/useSetThisWorkstation";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import {
  useDeleteWorkstationMutation,
  useGetWorkstationsQuery
} from "~/redux/warehouse/workstation.hooks";
import type { WorkstationSummaryDto } from "~/types/api";

const DELETE_TOKEN = "DELETE";

function WorkstationSelectRow({ ws }: { ws: WorkstationSummaryDto }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ minWidth: 0, width: "100%" }}
    >
      <Typography variant="body2" noWrap>
        {ws.deviceId}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {ws.autostoreGridName}
      </Typography>
    </Stack>
  );
}

export function WorkstationDangerZone() {
  const { t } = useTranslation();
  const { errorToast, successToast } = useToast();
  const [deleteInput, setDeleteInput] = useState("");

  const dispatch = useAppDispatch();
  const dropdownWorkstationId = useAppSelector(selectDropdownWorkstationId);
  const siteWorkstation = useAppSelector(selectThisWorkstation);

  const { data: workstations, isLoading: isLoadingWorkstations } =
    useGetWorkstationsQuery();
  const sortedWorkstations = useMemo(
    () =>
      workstations?.toSorted(
        (a, b) =>
          a.autostoreGridName.localeCompare(b.autostoreGridName) ||
          a.deviceId.localeCompare(b.deviceId)
      ) ?? [],
    [workstations]
  );

  const dropdownIdInList =
    Boolean(dropdownWorkstationId) &&
    sortedWorkstations.some((w) => w.id === dropdownWorkstationId);

  /** Same workstation as overview (`dropdownWorkstationId`), with a stable list value while Redux catches up. */
  const selectValue = dropdownIdInList
    ? dropdownWorkstationId
    : (sortedWorkstations[0]?.id ?? "");

  const selectedWorkstation = sortedWorkstations.find(
    (w) => w.id === selectValue
  );

  const isSelectedSiteWorkstation =
    !!siteWorkstation &&
    !!selectedWorkstation &&
    selectedWorkstation.id === siteWorkstation.id;

  const [deleteWorkstation, { isLoading: isDeleting }] =
    useDeleteWorkstationMutation();
  const { setThisWorkstation, isLoading: isSettingSite } =
    useSetThisWorkstation();

  const isDeleteInProgress = isDeleting || isSettingSite;

  const canSubmitDelete =
    deleteInput.trim() === DELETE_TOKEN &&
    !!selectedWorkstation &&
    !isDeleteInProgress;

  const handleTargetChange = (e: SelectChangeEvent) => {
    dispatch(setDropdownWorkstationId(e.target.value));
    setDeleteInput("");
  };

  const handleConfirmDelete = async () => {
    if (!canSubmitDelete || !selectedWorkstation) return;
    try {
      if (isSelectedSiteWorkstation) {
        await setThisWorkstation(null, true);
      }
      await deleteWorkstation({
        workstationId: selectedWorkstation.id
      }).unwrap();
      dispatch(setDropdownWorkstationId(""));
      successToast(t("workstation deleted toast"));
      setDeleteInput("");
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  };

  return (
    <Stack id="ws-danger-zone-root" spacing={2}>
      <OverviewFieldRow
        id="ws-danger-zone-target-row"
        labelId="danger-zone-workstation-label"
        label={t("danger zone target workstation")}
      >
        <Box
          id="ws-danger-zone-target-select-wrap"
          sx={{
            width: "100%",
            minWidth: 0,
            maxWidth: {
              xs: "100%",
              sm: settingsSiteSelectMaxWidthPx
            }
          }}
        >
          <FormControl fullWidth size="small" disabled={isLoadingWorkstations}>
            <Select<string>
              id="danger-zone-workstation-select"
              labelId="danger-zone-workstation-label"
              variant="outlined"
              value={selectValue}
              onChange={handleTargetChange}
              displayEmpty
              sx={settingsSelectSx}
              slotProps={{
                input: {
                  "aria-labelledby": "danger-zone-workstation-label"
                }
              }}
              renderValue={(id) => {
                const ws = sortedWorkstations.find((w) => w.id === id);
                if (!ws) {
                  return "";
                }
                return (
                  <Typography variant="body2" component="span" noWrap>
                    {ws.deviceId}
                  </Typography>
                );
              }}
            >
              {sortedWorkstations.map((ws) => (
                <MenuItem key={ws.id} value={ws.id}>
                  <WorkstationSelectRow ws={ws} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </OverviewFieldRow>

      <SettingsDangerZoneRow
        title={t("delete workstation")}
        hint={t("workstation delete caution")}
        confirmationPhrase={DELETE_TOKEN}
        confirmationValue={deleteInput}
        onConfirmationChange={setDeleteInput}
        actionLabel={t("delete workstation")}
        onAction={() => void handleConfirmDelete()}
        fieldDisabled={!selectedWorkstation}
        actionDisabled={!canSubmitDelete}
      />
    </Stack>
  );
}
